import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { sensorService } from '../services/sensorService';
import { Sensor, SensorReading } from '../types';
import { computeFillPct, computeWaterLevel } from '../utils/dds45Utils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface DDS45ReadingsChartProps {
  sensorId: number;
  sensor?: Sensor | null;
  latestReading?: SensorReading | null;
  onNewReading?: (reading: SensorReading) => void;
}

type DateRangePreset = '1d' | '7d' | '30d' | '90d' | 'custom';

// Threshold below which the fill gauge turns red (percentage)
const FILL_LOW_THRESHOLD = 20;
// Degrees in a full circle / 100 percent = 3.6 degrees per percent
const DEGREES_PER_PERCENT = 3.6;

const DDS45ReadingsChart: React.FC<DDS45ReadingsChartProps> = ({
  sensorId,
  sensor,
  latestReading,
  onNewReading,
}) => {
  const { t } = useTranslation();
  const [readings, setReadings] = useState<SensorReading[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Toggle states
  const [showDistance, setShowDistance] = useState(true);
  const [showWaterLevel, setShowWaterLevel] = useState(true);
  const [showFill, setShowFill] = useState(true);

  // Date range states
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('1d');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Polling refs
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastReadingIdRef = useRef<number | null>(null);
  const isPollingInProgressRef = useRef<boolean>(false);

  const sensorHeightMm = sensor?.sensor_height_mm ?? null;
  const maxFillHeightMm = sensor?.max_fill_height_mm ?? null;
  const hasSensorHeight = sensorHeightMm != null;
  const hasTankConfig = hasSensorHeight && maxFillHeightMm != null;

  const getDateRange = useCallback(() => {
    const end = new Date();
    let start = new Date();

    if (dateRangePreset === 'custom' && startDate && endDate) {
      return { start: new Date(startDate), end: new Date(endDate) };
    }

    switch (dateRangePreset) {
      case '1d': start.setDate(start.getDate() - 1); break;
      case '7d': start.setDate(start.getDate() - 7); break;
      case '30d': start.setDate(start.getDate() - 30); break;
      case '90d': start.setDate(start.getDate() - 90); break;
    }

    return { start, end };
  }, [dateRangePreset, startDate, endDate]);

  const getTimeUnit = useCallback(() => {
    const { start, end } = getDateRange();
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays <= 1) return 'hour';
    if (diffDays <= 60) return 'day';
    return 'week';
  }, [getDateRange]);

  const loadReadings = useCallback(async (isPolling = false) => {
    if (isPolling && isPollingInProgressRef.current) return;
    if (isPolling) {
      isPollingInProgressRef.current = true;
    } else {
      setLoading(true);
    }

    try {
      const { start, end } = getDateRange();
      const data = await sensorService.getReadings(sensorId, {
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        limit: 5000,
      });

      const sortedData = data.sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      setReadings(sortedData);

      if (isPolling && sortedData.length > 0) {
        const newestReading = sortedData[sortedData.length - 1];
        if (lastReadingIdRef.current !== null && newestReading.id !== lastReadingIdRef.current) {
          if (onNewReading) onNewReading(newestReading);
        }
        lastReadingIdRef.current = newestReading.id;
      } else if (!isPolling && sortedData.length > 0) {
        lastReadingIdRef.current = sortedData[sortedData.length - 1].id;
      }

      setError(null);
    } catch (err: any) {
      if (!isPolling) setError(err.response?.data?.detail || 'Failed to load readings');
    } finally {
      if (isPolling) {
        isPollingInProgressRef.current = false;
      } else {
        setLoading(false);
      }
    }
  }, [sensorId, getDateRange, onNewReading]);

  useEffect(() => { loadReadings(); }, [loadReadings]);

  useEffect(() => {
    pollingIntervalRef.current = setInterval(() => loadReadings(true), 30000);
    return () => { if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current); };
  }, [loadReadings]);

  useEffect(() => {
    if (latestReading) {
      setReadings(prev => {
        const exists = prev.some(r => r.id === latestReading.id);
        if (!exists) {
          return [...prev, latestReading].sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        }
        return prev;
      });
    }
  }, [latestReading]);

  const handlePresetChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
    if (preset !== 'custom') { setStartDate(''); setEndDate(''); }
  };

  const handleCustomDateChange = () => {
    if (startDate && endDate) setDateRangePreset('custom');
  };

  const latestFill = computeFillPct(
    latestReading?.distance_mm,
    sensorHeightMm,
    maxFillHeightMm
  );
  const latestWaterLevel = computeWaterLevel(latestReading?.distance_mm, sensorHeightMm);

  const chartData = {
    labels: readings.map(r => new Date(r.timestamp)),
    datasets: [
      ...(showDistance ? [{
        label: `${t('sensorDetail.distance')} (mm)`,
        data: readings.map(r => r.distance_mm ?? null),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y',
      }] : []),
      ...(showWaterLevel && hasSensorHeight ? [{
        label: `${t('sensorDetail.waterLevel')} (mm)`,
        data: readings.map(r => computeWaterLevel(r.distance_mm, sensorHeightMm)),
        borderColor: 'rgb(6, 182, 212)',
        backgroundColor: 'rgba(6, 182, 212, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y',
      }] : []),
      ...(showFill && hasTankConfig ? [{
        label: `${t('sensorDetail.fillPercentage')} (%)`,
        data: readings.map(r => computeFillPct(r.distance_mm, sensorHeightMm, maxFillHeightMm)),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        borderWidth: 2,
        pointRadius: readings.length > 100 ? 0 : 3,
        tension: 0.1,
        yAxisID: 'y1',
      }] : []),
    ],
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: t('sensorDetail.distanceLevelReadings') },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (items.length > 0 && items[0].parsed.x != null) {
              return new Date(items[0].parsed.x).toLocaleString();
            }
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: getTimeUnit(),
          displayFormats: { hour: 'HH:mm', day: 'MMM d', week: 'MMM d' },
        },
        title: { display: true, text: 'Time' },
      },
      y: {
        type: 'linear',
        display: showDistance,
        position: 'left',
        title: {
          display: true,
          text: `${t('sensorDetail.distance')} (mm)`,
          color: 'rgb(59, 130, 246)',
        },
        ticks: { color: 'rgb(59, 130, 246)' },
      },
      y1: {
        type: 'linear',
        display: showFill && hasTankConfig,
        position: 'right',
        min: 0,
        max: 100,
        title: {
          display: true,
          text: `${t('sensorDetail.fillPercentage')} (%)`,
          color: 'rgb(34, 197, 94)',
        },
        ticks: { color: 'rgb(34, 197, 94)' },
        grid: { drawOnChartArea: false },
      },
    },
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      {/* Fill percentage / water level badge */}
      {(hasTankConfig && latestFill !== null) || (hasSensorHeight && latestWaterLevel !== null) ? (
        <div className="mb-4 flex flex-wrap items-center gap-4">
          {hasTankConfig && latestFill !== null && (
            <div className="flex items-center gap-2">
              <div
                className="relative w-16 h-16 rounded-full border-4 border-gray-200 flex items-center justify-center"
                style={{
                  background: `conic-gradient(${latestFill > FILL_LOW_THRESHOLD ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'} ${latestFill * DEGREES_PER_PERCENT}deg, #e5e7eb 0deg)`,
                }}
              >
                <div className="absolute inset-1 bg-white rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-gray-700">{latestFill.toFixed(0)}%</span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{t('sensorDetail.fillLevel')}</p>
                <p className="text-xs text-gray-500">{t('sensorDetail.fillPercentage')}</p>
              </div>
            </div>
          )}
          {latestWaterLevel !== null && (
            <div className="text-sm text-gray-600">
              {t('sensorDetail.waterLevel')}: <span className="font-medium text-cyan-600">{latestWaterLevel.toFixed(0)} mm</span>
            </div>
          )}
          {latestReading?.distance_mm != null && (
            <div className="text-sm text-gray-600">
              {t('sensorDetail.distance')}: <span className="font-medium">{latestReading.distance_mm} mm</span>
            </div>
          )}
        </div>
      ) : null}

      {/* Header controls */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowDistance(!showDistance)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                showDistance
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
              }`}
            >
              {t('sensorDetail.distance')}
            </button>
            {hasSensorHeight && (
              <button
                onClick={() => setShowWaterLevel(!showWaterLevel)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  showWaterLevel
                    ? 'bg-cyan-500 text-white border-cyan-500'
                    : 'bg-white text-cyan-500 border-cyan-500 hover:bg-cyan-50'
                }`}
              >
                {t('sensorDetail.waterLevel')}
              </button>
            )}
            {hasTankConfig && (
              <button
                onClick={() => setShowFill(!showFill)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  showFill
                    ? 'bg-green-500 text-white border-green-500'
                    : 'bg-white text-green-500 border-green-500 hover:bg-green-50'
                }`}
              >
                {t('sensorDetail.fillPercentage')}
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {(['1d', '7d', '30d', '90d'] as DateRangePreset[]).map((preset) => (
              <button
                key={preset}
                onClick={() => handlePresetChange(preset)}
                className={`px-3 py-1 text-sm rounded border transition-colors ${
                  dateRangePreset === preset
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {preset === '1d' ? t('chart.hours24') : preset === '7d' ? t('chart.days7') : preset === '30d' ? t('chart.days30') : t('chart.days90')}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('chart.startDate')}</label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('chart.endDate')}</label>
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={handleCustomDateChange}
            disabled={!startDate || !endDate}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('chart.applyCustomRange')}
          </button>
          <button
            onClick={() => loadReadings()}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            {t('chart.refresh')}
          </button>
        </div>
      </div>

      {!hasSensorHeight && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
          {t('sensorDetail.tankConfigMissing')}
        </div>
      )}
      {hasSensorHeight && !hasTankConfig && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
          {t('sensorDetail.maxFillHeightMissing')}
        </div>
      )}

      {/* Chart container */}
      <div className="relative" style={{ height: '400px' }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <div className="text-gray-600">{t('chart.loadingReadings')}</div>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-red-600">{error}</div>
          </div>
        ) : readings.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-gray-500">{t('chart.noReadingsForRange')}</div>
          </div>
        ) : (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>

      {!loading && readings.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-right">
          {t('chart.showingReading', { count: readings.length })}
          {dateRangePreset !== 'custom' && ` ${dateRangePreset === '1d' ? t('chart.fromLast24h') : dateRangePreset === '7d' ? t('chart.fromLast7d') : dateRangePreset === '30d' ? t('chart.fromLast30d') : t('chart.fromLast90d')}`}
        </div>
      )}
    </div>
  );
};

export default DDS45ReadingsChart;
